import { useEffect, useState } from "react";

// Hand-rolled history-API navigation. The codebase already manipulates
// `window.history.pushState` directly from HomePage (see `handleReset`), so
// reach for this hook instead of pulling in `react-router-dom` for a single
// page.
//
// Subscribers receive updates on browser-driven `popstate` events as well as
// programmatic `navigate()` calls (which dispatch a synthetic event so all
// hook instances stay in sync).

const NAVIGATE_EVENT = "kareoke:navigate";

export function navigate(path: string): void {
  if (window.location.pathname !== path) {
    window.history.pushState(null, "", path);
  }
  window.dispatchEvent(new Event(NAVIGATE_EVENT));
}

export interface UseLocation {
  pathname: string;
  navigate: (path: string) => void;
}

export function useLocation(): UseLocation {
  const [pathname, setPathname] = useState<string>(() => window.location.pathname);

  useEffect(() => {
    const sync = () => setPathname(window.location.pathname);
    window.addEventListener("popstate", sync);
    window.addEventListener(NAVIGATE_EVENT, sync);
    return () => {
      window.removeEventListener("popstate", sync);
      window.removeEventListener(NAVIGATE_EVENT, sync);
    };
  }, []);

  // `navigate` is module-scoped and stable; no need to wrap in useCallback.
  return { pathname, navigate };
}

const SONG_ROUTE_RE = /^\/songs\/([0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12})\/?$/;
const ADMIN_ROUTE_RE = /^\/admin(?:\/([A-Za-z0-9_-]+))?\/?$/;

export function parseSongRoute(pathname: string): { jobId: string } | null {
  const match = SONG_ROUTE_RE.exec(pathname);
  if (!match) return null;
  return { jobId: match[1] };
}

export function parseAdminRoute(pathname: string): { section: string | null } | null {
  const match = ADMIN_ROUTE_RE.exec(pathname);
  if (!match) return null;
  return { section: match[1] ?? null };
}
